import React from 'react';
import { Link } from 'react-router-dom';
import './AboutPage.css';

const TIMELINE = [
  { year: '2026', label: 'LottoPickle launched', desc: 'Built from scratch as a data science platform using publicly available New York State lottery data.' },
  { year: '2026', label: 'Nine analytical views', desc: 'Frequency analysis, heatmaps, trend curves, distribution statistics, co-occurrence pairs, gap analysis, and probabilistic number generation.' },
  { year: 'Now', label: 'Ongoing', desc: 'Active development. New analytical views and data sources in progress.' },
];

const SKILLS = [
  { label: 'React 19', color: '#61dafb' },
  { label: 'Chart.js 4', color: '#ff6384' },
  { label: 'JavaScript (ES2022+)', color: '#f7df1e' },
  { label: 'Data Analysis', color: '#4ade80' },
  { label: 'Statistics', color: '#a78bfa' },
  { label: 'REST APIs', color: '#fb923c' },
  { label: 'CSS / Responsive Design', color: '#38bdf8' },
  { label: 'RevenueCat', color: '#facc15' },
];

export default function AboutPage() {
  return (
    <div className="about-page">

      {/* NAV */}
      <nav className="about-nav">
        <Link to="/" className="about-nav-logo">LottoPickle</Link>
        <div className="about-nav-right">
          <Link to="/app" className="about-nav-link">Open App</Link>
          <Link to="/data-science" className="about-nav-link">Data Science</Link>
          <Link to="/" className="about-nav-link">Home</Link>
        </div>
      </nav>

      {/* HERO */}
      <div className="about-hero">
        <span className="about-tag">About</span>
        <h1 className="about-title">
          Built by a developer.<br />
          <span className="about-title-accent">Powered by open data.</span>
        </h1>
        <p className="about-sub">
          LottoPickle is a data science project built on top of New York State's publicly available
          lottery dataset. The goal: demonstrate that a single data source, interrogated from multiple
          analytical angles, can produce genuinely useful and visually compelling insights.
        </p>
      </div>

      {/* PROJECT SECTION */}
      <div className="about-section">
        <div className="about-section-inner">
          <span className="about-tag">The Project</span>
          <h2 className="about-section-title">A data science platform<br /><em>disguised as a lottery app</em></h2>
          <div className="about-two-col">
            <div>
              <p>
                The premise is simple: take a publicly available dataset with well-understood
                properties (random draws, fixed number ranges, long history) and apply every
                major data science technique to it — frequency analysis, time series, inferential
                statistics, combinatorics, probabilistic modeling.
              </p>
              <p>
                The lottery framing makes the analysis accessible and fun. But the underlying
                methods — heatmap encoding, cumulative trend curves, co-occurrence hash maps,
                skewness coefficients — are the same techniques used in finance, genomics,
                retail analytics, and recommendation systems.
              </p>
            </div>
            <div>
              <p>
                All computation runs client-side in the browser using React's <code>useMemo</code> hooks,
                eliminating the need for a backend entirely. Data is fetched live from the
                New York State SODA API — up to 5,000 draw records per game, parsed and
                aggregated on the fly.
              </p>
              <p>
                The result is a fully functional, zero-latency analytics dashboard that
                demonstrates practical application of data science concepts in a production
                web environment.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* TIMELINE */}
      <div className="about-timeline-section">
        <div className="about-section-inner">
          <span className="about-tag">Timeline</span>
          <h2 className="about-section-title">Project history</h2>
          <div className="about-timeline">
            {TIMELINE.map((item, i) => (
              <div className="about-timeline-item" key={i}>
                <div className="about-tl-year">{item.year}</div>
                <div className="about-tl-dot"></div>
                <div className="about-tl-content">
                  <h3 className="about-tl-label">{item.label}</h3>
                  <p className="about-tl-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* SKILLS */}
      <div className="about-skills-section">
        <div className="about-section-inner">
          <span className="about-tag">Tech & Skills</span>
          <h2 className="about-section-title">What this project demonstrates</h2>
          <div className="about-skills-grid">
            {SKILLS.map((s) => (
              <div className="about-skill-chip" key={s.label} style={{ borderColor: `${s.color}33` }}>
                <span className="about-skill-dot" style={{ background: s.color }}></span>
                {s.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* DATA SOURCE */}
      <div className="about-data-section">
        <div className="about-section-inner about-data-grid">
          <div>
            <span className="about-tag">Data Source</span>
            <h2 className="about-section-title">New York State Open Data</h2>
            <p>
              All analysis uses historical Powerball and Mega Millions draw data from
              the New York State government open data portal (data.ny.gov). The dataset
              is publicly available, free to access, and updated after every draw.
            </p>
            <p>
              Data is fetched via the SODA REST API, requesting up to 5,000 records
              ordered chronologically. No data is stored — everything is computed fresh
              on each session in the user's browser.
            </p>
            <Link to="/data-science" className="about-link-btn">Read the full methodology →</Link>
          </div>
          <div className="about-data-stats">
            <div className="about-stat">
              <span className="about-stat-num">~2,800</span>
              <span className="about-stat-label">Powerball draws</span>
            </div>
            <div className="about-stat">
              <span className="about-stat-num">~2,900</span>
              <span className="about-stat-label">Mega Millions draws</span>
            </div>
            <div className="about-stat">
              <span className="about-stat-num">9</span>
              <span className="about-stat-label">Analytical views</span>
            </div>
            <div className="about-stat">
              <span className="about-stat-num">$0</span>
              <span className="about-stat-label">Backend cost</span>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="about-cta">
        <h2>See the analysis in action</h2>
        <p>All nine views are live — free to explore.</p>
        <div className="about-cta-actions">
          <Link to="/app" className="nf-btn-primary">Open LottoPickle</Link>
          <a href="mailto:info@lottopickle.com" className="nf-btn-secondary">Get in touch</a>
        </div>
      </div>

      {/* FOOTER */}
      <footer className="about-footer">
        <div className="about-footer-links">
          <Link to="/">Home</Link>
          <Link to="/app">App</Link>
          <Link to="/data-science">Data Science</Link>
          <Link to="/pricing">Pricing</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <a href="mailto:info@lottopickle.com">Contact</a>
        </div>
        <p className="about-footer-disclaimer">For entertainment and data analysis purposes only. Not gambling advice.</p>
      </footer>
    </div>
  );
}
